import React from 'react'
import Table from '@/components/table/Table'
import Row from '@/components/table/Row'
import CellList from '@/components/table/CellList'
import PopupDialog from '@/components/popupDialog/PopupDialog'
import AddField from '@/components/addField/AddField'
import PopupSelect from '@/components/popupSelect/PopupSelect'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DescriptionIcon from '@mui/icons-material/Description'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import Switch from '@mui/material/Switch'
import { useLazyGetSchemaQuery } from '@/store/apis/schemas'
import {
  useLazyGetScenesQuery,
  useLazyAddSceneQuery,
  useLazyDeleteSceneQuery,
  useLazyUpdateSceneQuery,
  useLazyAddUsersToScenQuery,
  useLazyRemoveUserFromScenQuery,
  useLazyUpdateSortOrderQuery,
  ISceneCreate,
  ISceneUpdate,
  IScene,
} from '@/store/apis/scenes'
import { useLazyGetUsersQuery, IUserObject } from '@/store/apis/users'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CSVLink } from 'react-csv'
import PdfScenes from './PdfScenes'

interface IState {
  selectedPopup?: 'ADD' | 'DELETE' | 'ADD_USERS' | 'REMOVE_USER' | 'CREATE_PDF'
  deleteScene?: IScene
  addScene?: ISceneCreate
  editScene?: ISceneUpdate
  removeUser?: IUserObject
  addSceneUsers?: IUserObject[]
  selectedScene?: IScene
  csvData?: string[][]
}

const Scenes: React.FC = () => {
  const { t } = useTranslation()
  const urlParams = useParams()
  const [getSchemaQuery, { data: schemaData, isLoading: isGettingSchema }] =
    useLazyGetSchemaQuery()
  const [getScenesQuery, { data: scenesData, isLoading: isGettingScenes }] =
    useLazyGetScenesQuery()
  const [addSceneQuery, { isFetching: isAddingScene }] = useLazyAddSceneQuery()
  const [updateSceneQuery, { isFetching: isUpdatingScene }] =
    useLazyUpdateSceneQuery()
  const [deleteSceneQuery, { isFetching: isDeletingScene }] =
    useLazyDeleteSceneQuery()
  const [addUsersToScen, { isFetching: isAddingUsers }] =
    useLazyAddUsersToScenQuery()
  const [removeUserFromScen, { isFetching: isRemovingUser }] =
    useLazyRemoveUserFromScenQuery()
  const [updateSortOrderQuery] = useLazyUpdateSortOrderQuery()
  const [getUsersQuery] = useLazyGetUsersQuery()
  const [state, setState] = React.useState<IState>({})
  const [scenes, setScenes] = React.useState<IScene[]>()
  const schemaId = urlParams?.schemaId ? Number(urlParams.schemaId) : -1

  React.useEffect(() => {
    if (urlParams?.schemaId) {
      getSchemaQuery({ id: schemaId })
      getScenesQuery({ schemaId })
    }
    getUsersQuery({})
  }, [getSchemaQuery, getScenesQuery, urlParams, getUsersQuery])

  React.useEffect(() => {
    if (scenesData && schemaData) {
      let csvData = [[t('name'), t('users')]]
      csvData = csvData.concat(
        scenesData.items.map((scene) => [
          scene.scene.name,
          scene.users.length < schemaData.users.length
            ? scene.users
                .map((user) => `${user.firstName} ${user.lastName}`)
                .join(', ')
            : t('pdf.all').toUpperCase(),
        ]),
      )

      setState({
        ...state,
        csvData,
      })
      setScenes(scenesData.items)
    }
  }, [scenesData, schemaData])

  const handleAdd = async (): Promise<void> => {
    if (state.addScene) {
      await addSceneQuery({ schemaId, ...state.addScene })
      if (urlParams?.schemaId) {
        getScenesQuery({ schemaId })
      }
      setState({
        ...state,
        addScene: undefined,
        selectedPopup: undefined,
      })
    }
  }

  const handleDelete = async (id: number): Promise<void> => {
    await deleteSceneQuery({ schemaId, id })
    if (urlParams?.schemaId) {
      getScenesQuery({ schemaId })
    }
    setState({
      ...state,
      selectedPopup: undefined,
      deleteScene: undefined,
    })
  }

  const handleEdit = async (): Promise<void> => {
    if (state.editScene) {
      await updateSceneQuery({ schemaId, ...state.editScene })
      if (urlParams?.schemaId) {
        getScenesQuery({ schemaId })
      }
      setState({ ...state, editScene: undefined })
    }
  }

  const handleAddUsers = async (ids: number[]) => {
    if (state.selectedScene) {
      const filteredIds = ids.filter(
        (id) =>
          state.selectedScene.users.findIndex((user) => user.id === id) === -1,
      )
      if (filteredIds.length) {
        await addUsersToScen({
          schemaId,
          id: state.selectedScene.scene.id,
          userIds: filteredIds,
        })
        if (urlParams?.schemaId) {
          getScenesQuery({ schemaId })
        }
        setState({
          ...state,
          selectedScene: undefined,
          selectedPopup: undefined,
        })
      }
    }
  }

  const handleUserRemove = async (id: number): Promise<void> => {
    if (state.selectedScene) {
      await removeUserFromScen({ id: state.selectedScene.scene.id, userId: id })
      if (urlParams?.schemaId) {
        getScenesQuery({ schemaId })
      }
      setState({
        ...state,
        selectedScene: undefined,
        selectedPopup: undefined,
      })
    }
  }

  const handleDragEnd = async (dropResult: DropResult) => {
    if (dropResult.destination && scenes && schemaData) {
      const tempData = [...scenes]
      const item = tempData.splice(dropResult.source.index, 1)[0]
      tempData.splice(dropResult.destination.index, 0, item)
      const sortIds = tempData.map((scene, index) => ({
        id: scene.scene.id,
        sortId: index,
      }))
      setScenes(tempData)
      await updateSortOrderQuery({ schemaId, sortIds })
      await getScenesQuery({ schemaId })
    }
  }

  const handleChangePriority = async (
    sceneId: number,
    userId: number,
    prioritised: boolean,
  ) => {
    if (scenes) {
      const scene = scenes.find((scene) => scene.scene.id === sceneId)
      if (scene) {
        const userPriorities = [
          ...(scene.scene.userPriorities ? scene.scene.userPriorities : []),
        ]
        if (prioritised) {
          userPriorities.push(userId)
        } else {
          const priorityIndex = userPriorities.findIndex(
            (priorityUserId) => priorityUserId === userId,
          )
          if (priorityIndex !== -1) {
            userPriorities.splice(priorityIndex, 1)
          }
        }
        const tempScenes = [...scenes]
        const foundSceneId = scenes.findIndex(
          (scene) => scene.scene.id === sceneId,
        )
        const [sourceData] = tempScenes.splice(foundSceneId, 1)
        const newSceneData = {
          ...sourceData,
          userPriorities,
        }
        tempScenes.splice(foundSceneId, 0, newSceneData)
        // TODO: Fix faster temporary switch for user
        setScenes(tempScenes)
        await updateSceneQuery({ schemaId, id: sceneId, userPriorities })
        await getScenesQuery({ schemaId })
      }
    }
  }

  const {
    selectedPopup,
    deleteScene,
    addScene,
    editScene,
    removeUser,
    addSceneUsers,
    selectedScene,
    csvData,
  } = state

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="tableBodyDropArea">
          {(dropProvider) => (
            <Table
              dropRef={dropProvider.innerRef}
              dropProps={dropProvider.droppableProps}
              headerLabels={[
                t('headers.name'),
                t('headers.numberOfUsers'),
                t('headers.numberOfTimeslots'),
              ]}
              headerActions={[
                {
                  icon: <PictureAsPdfIcon />,
                  disabled: isGettingSchema || isGettingScenes,
                  onClick: () => {
                    setState({
                      ...state,
                      selectedPopup: 'CREATE_PDF',
                    })
                  },
                },
                {
                  icon: (
                    <CSVLink
                      data={csvData ?? []}
                      style={{ color: 'inherit' }}
                      filename={`${t('scenesPdf.title')} - ${schemaData?.schema.schema.name}.csv`}
                    >
                      <DescriptionIcon />
                    </CSVLink>
                  ),
                  disabled: isGettingSchema || isGettingScenes,
                },
              ]}
              showExpand={true}
              isLoading={isGettingSchema || isGettingScenes}
              isEditing={!!editScene}
              isAddOpen={selectedPopup === 'ADD'}
              onAddOpen={() =>
                schemaData?.schema &&
                setState({
                  ...state,
                  selectedPopup: 'ADD',
                  addScene: {
                    name: '',
                  },
                })
              }
            >
              {schemaData?.schema &&
                scenes?.map((scene, index) => (
                  <Draggable
                    key={scene.scene.name}
                    draggableId={scene.scene.name}
                    index={index}
                  >
                    {(dragProvider) => (
                      <Row
                        dragProps={dragProvider.draggableProps}
                        dragHandleProps={dragProvider.dragHandleProps}
                        dragRef={dragProvider.innerRef}
                        sortId={index}
                        showExpand={true}
                        isLoading={isUpdatingScene}
                        disableButtons={!!editScene}
                        onEditOpen={() =>
                          setState({ ...state, editScene: scene.scene })
                        }
                        onEditSuccess={() => handleEdit()}
                        onEditAbort={() =>
                          setState({ ...state, editScene: undefined })
                        }
                        onDeleteOpen={() =>
                          setState({
                            ...state,
                            selectedPopup: 'DELETE',
                            deleteScene: scene,
                          })
                        }
                        columns={{
                          normal: [
                            scene.scene.name,
                            <CellList
                              value={scene.users.length}
                              list={scene.users.map((user) => {
                                const priorityIcon =
                                  scene.scene.userPriorities?.findIndex(
                                    (priority) => priority === user.id,
                                  ) !== -1 && (
                                    <PriorityHighIcon
                                      sx={{ color: '#F5BF25' }}
                                    />
                                  )
                                return (
                                  <div>
                                    {priorityIcon}
                                    {`${user.firstName} ${user.lastName}`}
                                  </div>
                                )
                              })}
                            />,
                            scene.scene.timeslotCount,
                          ],
                          edit: editScene && [
                            <AddField
                              value={editScene.name}
                              required={true}
                              regex={/^$/}
                              onChange={(newValue) =>
                                setState({
                                  ...state,
                                  editScene: { ...editScene, name: newValue },
                                })
                              }
                            />,
                            <CellList
                              value={scene.users.length}
                              list={scene.users.map(
                                (user) => `${user.firstName} ${user.lastName}`,
                              )}
                            />,
                            scene.scene.timeslotCount,
                          ],
                        }}
                      >
                        <Table
                          isLoading={false}
                          headerLabels={[
                            t('headers.firstName'),
                            t('headers.sureName'),
                            t('headers.prioritised'),
                          ]}
                          onAddOpen={() => {
                            setState({
                              ...state,
                              selectedPopup: 'ADD_USERS',
                              addSceneUsers: scene.users,
                              selectedScene: scene,
                            })
                          }}
                        >
                          {scene.users.map((user, index) => {
                            const isPrioritised =
                              scene.scene.userPriorities?.findIndex(
                                (priority) => priority === user.id,
                              ) !== -1
                            return (
                              <Row
                                key={index}
                                onDeleteOpen={() =>
                                  setState({
                                    ...state,
                                    selectedPopup: 'REMOVE_USER',
                                    removeUser: user,
                                    selectedScene: scene,
                                  })
                                }
                                columns={{
                                  normal: [
                                    user.firstName,
                                    user.lastName,
                                    <Switch
                                      color="primary"
                                      disabled={isUpdatingScene}
                                      checked={isPrioritised}
                                      onClick={(event) => {
                                        event.stopPropagation()
                                        handleChangePriority(
                                          scene.scene.id,
                                          user.id,
                                          !isPrioritised,
                                        )
                                      }}
                                    />,
                                  ],
                                }}
                              />
                            )
                          })}
                        </Table>
                      </Row>
                    )}
                  </Draggable>
                ))}
              {dropProvider.placeholder}
              {selectedPopup === 'ADD' && addScene && (
                <Row
                  onAddSuccess={() => handleAdd()}
                  onAddAbort={() =>
                    setState({
                      ...state,
                      addScene: undefined,
                      selectedPopup: undefined,
                    })
                  }
                  isLoading={isAddingScene}
                  columns={{
                    normal: [
                      '',
                      '',
                      <AddField
                        value={addScene.name}
                        required={true}
                        regex={/^$/}
                        onChange={(newValue) =>
                          setState({
                            ...state,
                            addScene: { ...addScene, name: newValue },
                          })
                        }
                      />,
                      0,
                      0,
                    ],
                  }}
                />
              )}
            </Table>
          )}
        </Droppable>
      </DragDropContext>
      <PopupDialog
        isOpen={selectedPopup === 'DELETE'}
        title={
          <>
            {t('popups.removeSceneTitle')}: <b>{deleteScene?.scene.name}</b>
          </>
        }
        content={t('popups.removeSceneDescription')}
        isLoading={isDeletingScene}
        onClose={() => setState({ ...state, selectedPopup: undefined })}
        onConfirm={() =>
          deleteScene?.scene.id ? handleDelete(deleteScene.scene.id) : null
        }
      />
      <PopupDialog
        isOpen={selectedPopup === 'REMOVE_USER'}
        title={
          <>
            {t('popups.removeUserFromSceneTitle')}:{' '}
            <b>{removeUser?.firstName}</b>
          </>
        }
        content={t('popups.removeUserFromSceneDescription')}
        isLoading={isRemovingUser}
        onClose={() => setState({ ...state, selectedPopup: undefined })}
        onConfirm={() =>
          removeUser?.id ? handleUserRemove(removeUser.id) : null
        }
      />
      {addSceneUsers && selectedScene && schemaData && (
        <PopupSelect
          isOpen={selectedPopup === 'ADD_USERS'}
          title={t('popups.chooseUsersToScene')}
          selectedName={selectedScene.scene.name}
          selections={schemaData.users.map((user) => ({
            id: user.id,
            name: `${user.firstName} ${user.lastName}`,
            defaultSelected:
              selectedScene.users.findIndex(
                (sceneUser) => sceneUser.id === user.id,
              ) !== -1,
            disabled:
              selectedScene.users.findIndex(
                (sceneUser) => sceneUser.id === user.id,
              ) !== -1,
          }))}
          isLoading={isAddingUsers}
          onClose={() =>
            setState({
              ...state,
              selectedPopup: undefined,
              addSceneUsers: undefined,
              selectedScene: undefined,
            })
          }
          onConfirm={(userIds) => handleAddUsers(userIds)}
        />
      )}
      {scenes && schemaData && (
        <PopupDialog
          isOpen={selectedPopup === 'CREATE_PDF'}
          title={t('popups.createPdfTitle')}
          content={
            <PdfScenes
              schemaName={schemaData.schema.schema.name}
              schemaUserCount={schemaData.users.length}
              scenes={scenes}
            />
          }
          disableMaxWidth
          isLoading={false}
          onClose={() =>
            setState({
              ...state,
              selectedPopup: undefined,
            })
          }
        />
      )}
    </>
  )
}

export default Scenes
